<template>
  <v-row
    class="pb-2"
    no-gutters
    style="display: flex; flex-direction: column; align-items: center">
    <h2 class="text-center pb-4">
      {{ headerText }}
    </h2>
    <v-col :class="userPaymentState === USER_PAYMENT_STATES.eligibleForTrial ? 'mt-2 mb-2' : 'stripe-card-container mb-3'">
      <form
        v-show="userPaymentState !== USER_PAYMENT_STATES.eligibleForTrial"
        id="payment-form"
        class="sr-payment-form">
        <div class="sr-combo-inputs-row">
          <div
            id="card-element"
            data-test="creditcard_number_input_field"
            class="sr-input sr-card-element"
            style="padding: 1rem;background: #fff;border-radius: 0.5rem;" />
        </div>
        <p
          id="card-errors"
          class="x-small-p red--text"
          role="alert">
          {{ error }}
        </p>
      </form>
      <v-radio-group
        v-show="userPaymentState !== USER_PAYMENT_STATES.eligibleForTrial"
        v-model="selectedPrice"
        style="padding:10px 20px 0px 20px">
        <div
          v-for="(plan, i) in filteredSortedPrices"
          :key="i"
          class="radio-wrapper">
          <v-radio
            color="blue"
            :label="getPlanTitle(plan)"
            :value="plan.id"
            @change="selectIndex(i)" />
        </div>
        <span
          v-if="selectedPrice"
          class="mt-2"
          style="white-space: pre-line;">
          {{ priceNoticeText }}
        </span>
      </v-radio-group>
      <v-row
        no-gutters
        justify="center"
        style="padding-bottom: 20px;">
        <v-btn
          :loading="loading"
          data-test="pay_button"
          color="blue"
          class="payButton elevation-0 white--text"
          @click="pay(selectedPrice)">
          {{ payButtonText }}
        </v-btn>
      </v-row>
    </v-col>
  </v-row>
</template>
<script>
import PaymentsApi from '@/services/graphql/payments';
import {
  mapActions, mapGetters, mapMutations,
} from 'vuex';
import currencyController from '@/mixins/currencyController';
import { USER_PAYMENT_STATES } from '@/constants';
export default {
  name: 'PaymentsCard',
  mixins: [currencyController],
  props: {
    sortedPrices: {
      type: Array,
      default: () => [],
    },
    userSubscriptions: {
      type: Array,
      default: () => [],
    },
    userPaymentState: {
      type: String,
      default: USER_PAYMENT_STATES.subActive,
    },
    radioRangeBlock: {
      type: Boolean,
      default: () => false,
    },
    usedLicenses: {
      type: Number,
      default: () => 0,
    },
    selectedPlan: {
      type: Number,
      default: 0,
    },
    isWorkspacePaymentsCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      stripe: null,
      card: null,

      selectedPrice: null,
      error: '',
      loading: false,
      indexPlan: null,
      stripeDataTest: [
        {
          name: 'creditcard_number_input_field',
          inputName: 'cardnumber',
        },
        {
          name: 'creditcard_date_input_field',
          inputName: 'cardExpiry',
        },
        {
          name: 'creditcard_cvc_input_field',
          inputName: 'cvc',
        },
      ],
      USER_PAYMENT_STATES: USER_PAYMENT_STATES,
    };
  },
  computed: {
    ...mapGetters('Payment', ['getPreSelectedRole']),
    findPreSelectedRole() {
      const preSelectedRole = this.getPreSelectedRole;
      return (
        preSelectedRole &&
        this.filteredSortedPrices.find(
          e =>
            e?.nickname.includes(preSelectedRole?.role) &&
            e?.recurring.interval === preSelectedRole?.interval
        )?.id
      );
    },
    filteredSortedPrices() {
      if (this.sortedPrices && this.sortedPrices.length > 0) {
        return this.sortedPrices.filter(plan => this.isFullObject(plan));
      } else {
        return [];
      }
    },
    availablePlanDate() {
      const yearOrMonth = this.activePlan?.recurring?.interval === 'year';
      const today = this.$moment().format('YYYY-MM-DD');
      const currentDate = this.$moment(today);
      if (yearOrMonth) {
        return this.$moment(currentDate)
          .add(1, 'Y')
          .format('DD-MM-YYYY');
      }
      return this.$moment(currentDate)
        .add(1, 'M')
        .format('DD-MM-YYYY');
    },
    activePlan() {
      return this.sortedPrices.find(el => el.id === this.selectedPrice);
    },
    headerText() {
      if (this.userPaymentState === USER_PAYMENT_STATES.eligibleForTrial) {
        return 'Confirm Your 30-Day Free Trial';
      } else if (this.userPaymentState === USER_PAYMENT_STATES.trialEnded
                || this.userPaymentState === USER_PAYMENT_STATES.trialing) {
        return 'Activate Your Plan To Continue Using Your Workspace';
      } else {
        return 'Complete Subscription';
      }
    },
    priceNoticeText() {
      let renewalDate = this.availablePlanDate;
      let trialCarryOver = false;

      if (this.userSubscriptions && this.userSubscriptions.length) {
        const latest = this.userSubscriptions.reduce((latestSub, currentSub) => {
          const latestDate = new Date(latestSub.subEnd);
          const currentDate = new Date(currentSub.subEnd);
          return currentDate > latestDate ? currentSub : latestSub;
        });

        const date = new Date(latest.subEnd);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');

        renewalDate = `${yyyy}-${mm}-${dd}`;
        if (latest.trialing) trialCarryOver = true;
      }

      return trialCarryOver
        ? `Trial period will be carried forward.\nSubscription will be renewed on ${renewalDate}`
        : `Subscription will be renewed on ${renewalDate}`;
    },
    payButtonText() {
      if (this.userPaymentState === USER_PAYMENT_STATES.eligibleForTrial) {
        return 'Start Trial';
      } else if (this.userPaymentState === USER_PAYMENT_STATES.trialEnded
                  || this.userPaymentState === USER_PAYMENT_STATES.trialing) {
        return 'Activate My Plan';
      } else {
        return 'Pay';
      }
    },
  },
  watch: {
    findPreSelectedRole(id) {
      if (id) {
        this.selectedPrice = id;
      }
    },
    selectedPlan() {
      const { sortedPrices, indexPlan } = this;
      if (indexPlan !== null) {
        this.selectedPrice = sortedPrices[indexPlan].id;
      }
    },
  },
  created() {
    //if we set view in vuex before , then we have view on back and need remove the view
    this.getPreSelectedRole && this.removeViewParam();
  },
  async mounted() {
    await this.initCurrencyController();
    this.setupElements();
  },
  methods: {
    ...mapActions({
      handleError: 'handleError',
    }),
    ...mapMutations(['openSnackBar']),
    async removeViewParam() {
      try {
        await PaymentsApi.deleteViewParams({
          viewId: 'payment',
        });
      } catch (err) {
        this.handleError(err);
      }
    },
    selectIndex(i) {
      this.indexPlan = i;
    },
    isFullObject(plan) {
      return plan && Object.values(plan).every(item => item);
    },
    async pay(selectedPrice) {
      const isTrial = this.userPaymentState === USER_PAYMENT_STATES.eligibleForTrial;
      if (isTrial) {
        const monthly = this.filteredSortedPrices.find(p => p.recurring.interval === 'month');
        this.selectedPrice = monthly.id;
        selectedPrice = monthly.id;
      }
      if (!selectedPrice) return;

      this.loading = true;
      this.error = '';
      try {
        const { currency = '' } = this.currencyController || {
        };
        let paymentMethodId = '';
        if (!isTrial) {
          paymentMethodId = await this.createAndAttachPaymentMethod(currency);
        }
        this.$emit(
          'stripeCreateWorkspaceSubscribe',
          selectedPrice,
          paymentMethodId,
          currency,
        );
        this.$emit('paymentModel', false);
      } catch (err) {
        this.error = err;
        this.loading = false;
        console.log('Error making payment', err);
        this.openSnackBar({
          text: 'Error making payment. Please try again. If failure persists, please contact support',
        });
      } finally {
        this.loading = false;
      }
    },
    async createAndAttachPaymentMethod(currency) {
      const createPMResponse = await this.stripe.createPaymentMethod({
        type: 'card',
        card: this.card,
      });
      if (createPMResponse.error) {
        throw new Error(createPMResponse.error.message);
      }
      const paymentMethodId = createPMResponse.paymentMethod.id;
      const attachPMResp = await PaymentsApi.saveAndAttachPaymentMethodId({
        paymentMethodId: paymentMethodId,
        currency,
      });
      if (!attachPMResp.data.response.success === true) {
        throw new Error(attachPMResp.data.response.message);
      }
      return paymentMethodId;
    },
    getPlanTitle({ recurring, unit_amount = 1, nickname } = {
    }) {
      if (!this.currencyController) {
        console.error('Unable to get plan title because currencyController is missing!');
        return '';
      }
      let title = nickname;
      const price = this.currencyController.formatCurrency(unit_amount / 100);
      const { interval } = recurring ?? {
      };
      let planTitle = `${title} - ${price} / ${interval}`;
      if (interval === 'year') {
        const pricePerMonth = this.currencyController.formatCurrency(+unit_amount / 12 / 100);
        planTitle += ` (${pricePerMonth} / month)`;
      }
      return planTitle;
    },
    setupElements() {
      // const stripe = window.Stripe('pk_test_51Hvg8oHTOrfLh7WUcFpVn6kXzpRWIVdVe0fYw1Q6c5XDR6NmTDC6liWwS8SzzZaKShUDqAr5pUerQisxH0QZmXJN00myTqtEnU');
      const stripe = window.Stripe(process.env.VUE_APP_STRIPE_API_KEY);

      /* ------- Set up Stripe Elements to use in checkout form ------- */
      const elements = stripe.elements();
      const style = {
        base: {
          color: '#8CA3A9',
          fontFamily: '"Roboto", sans-serif',
          backgroundColor: '#fff',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a',
        },
      };
      const card = elements.create('card', {
        style,
      });
      card.mount('#card-element');
      this.stripe = stripe;
      this.card = card;
      card.on('change', e => {
        if (e.error) {
          this.error = e.error.message;
        } else {
          this.error = '';
        }
      });
    },
  },
};
</script>
<style lang="scss" scoped></style>
